import React from 'react';
import numbers from '~/utils/numbers';
import treatNotes from './treatNotes';
import InfoTooltip from './InfoTooltip';
import { tooltipTexts } from './tooltipTexts';
import './style.css';

// Refs: ¹²³⁴⁵⁶⁷⁸⁹

interface Props {
    data: any;
    calc: any;
}

const MercadoLivreBox: React.FC<Props> = (props) => {
    const data = props.data ?? {};
    const calc = props.calc ?? {};

    const safeDemands = data?.demands ?? {
        consumed: { peak: 0, offPeak: 0 },
        notConsumed: { peak: 0, offPeak: 0 },
        overtaking: { peak: 0, offPeak: 0 },
    };
    const safeOthers = data?.others ?? { cative: 0, freeMarket: 0 };
    const safeData = {
        ...data,
        modality: data?.modality ?? 'Verde',
        demands: safeDemands,
        others: safeOthers,
        consumptionPeak: data?.consumptionPeak ?? 0,
        consumptionOffPeak: data?.consumptionOffPeak ?? 0,
        consumptionTE: data?.consumptionTE ?? 0,
        pisCofins: data?.pisCofins ?? 0,
        icms: data?.icms ?? 0,
        proinfa: data?.proinfa ?? null,
        covid: data?.covid ?? 0,
    };

    // Init treat notes
    try {
        treatNotes.init(safeData, calc);
    } catch (e) {
        console.log('treatNotes.init failed (MercadoLivre):', e);
    }

    const isFiniteNum = (v: any) => {
        const n = typeof v === 'number' ? v : Number(v);
        return Number.isFinite(n);
    };

    const teAclAmount = calc?.teAclAmount ?? null;
    const teAclTariff = calc?.teAclTariff ?? null;
    const teAclValue = calc?.teAclValue ?? null;

    const perdaTransAmount = calc?.perdaTransAmount ?? null;
    const perdaTransTariff = calc?.perdaTransTariff ?? null;
    const perdaTransValue = calc?.perdaTransValue ?? calc?.perdaTrans ?? null;

    const proinfaAmount = calc?.proinfaAmount ?? null;
    const proinfaTariff = calc?.proinfaTariff ?? null;
    const proinfaValue = calc?.proinfaValue ?? calc?.proinfa ?? null;

    const hasTeACL = teAclAmount !== null && teAclAmount !== undefined && isFiniteNum(teAclAmount);
    const hasPerdaTrans = perdaTransAmount !== null && perdaTransAmount !== undefined && isFiniteNum(perdaTransAmount);

    const hasProinfaAmount = proinfaAmount !== null && proinfaAmount !== undefined && isFiniteNum(proinfaAmount);
    const hasProinfaAny = hasProinfaAmount || isFiniteNum(proinfaTariff) || isFiniteNum(proinfaValue);

    return (
        <div className="freeMarket">
            <h2>Custo do Mercado Livre</h2>

            <div className="body">
                <div className="line title">
                    <div className="label"></div>
                    <div className="qtty">Quant.</div>
                    <div className="mult">Tarifas</div>
                    <div className="value">Valor</div>
                </div>

                {
                    safeData.modality==='Verde' ? (
                        <>
                            <div className="line">
                                <div className="label">
                                    Demanda Única (KW)
                                    <InfoTooltip text={tooltipTexts.demandaUnica} />
                                </div>
                                <div className="qtty">{ numbers.format(safeData.demands.consumed.offPeak) }</div>
                                <div className="mult">{ numbers.format(calc.demandUniqueMult, true, 5) }</div>
                                <div className="value">{ numbers.money(calc.demandUnique, false) }</div>
                            </div>

                            { safeData.demands.notConsumed.offPeak !== 0 &&
                                <div className="line">
                                    <div className="label">
                                        Demanda Única Sem ICMS (KW)
                                        <InfoTooltip text={tooltipTexts.demandaSemIcms} />
                                    </div>
                                    <div className="qtty">{ numbers.format(safeData.demands.notConsumed.offPeak) }</div>
                                    <div className="mult">{ numbers.format(calc.demandUniqueMult, true, 5) }</div>
                                    <div className="value">{ numbers.money(calc.demandNoIcmsUnique, false) }</div>
                                </div>
                            }

                            { safeData.demands.overtaking.offPeak!==0 &&
                                <div className="line">
                                    <div className="label">
                                        Ultrapassagem de demanda única (KW)
                                        <InfoTooltip text={tooltipTexts.ultrapassagemDemanda} />
                                    </div>
                                    <div className="qtty">{ numbers.format(safeData.demands.overtaking.offPeak) }</div>
                                    <div className="mult">{ numbers.format(calc.demandOvertakingUniqueMult, true, 5) }</div>
                                    <div className="value">{ numbers.money(calc.demandOvertakingUnique, false) }</div>
                                </div>
                            }
                        </>
                    ) : (
                        <>
                            <div className="line">
                                <div className="label">
                                    Demanda Ponta (KW)
                                    <InfoTooltip text={tooltipTexts.demandaPonta} />
                                </div>
                                <div className="qtty">{ numbers.format(safeData.demands.consumed.peak) }</div>
                                <div className="mult">{ numbers.format(calc.demandPeakMult, true, 5) }</div>
                                <div className="value">{ numbers.money(calc.demandPeak, false) }</div>
                            </div>

                            <div className="line">
                                <div className="label">
                                    Demanda Fora Ponta (KW)
                                    <InfoTooltip text={tooltipTexts.demandaForaPonta} />
                                </div>
                                <div className="qtty">{ numbers.format(safeData.demands.consumed.offPeak) }</div>
                                <div className="mult">{ numbers.format(calc.demandOffPeakMult, true, 5) }</div>
                                <div className="value">{ numbers.money(calc.demandOffPeak, false) }</div>
                            </div>

                            <div className="line">
                                <div className="label">
                                    Demanda Ponta Sem ICMS (KW)
                                    <InfoTooltip text={tooltipTexts.demandaSemIcms} />
                                </div>
                                <div className="qtty">{ numbers.format(safeData.demands.notConsumed.peak) }</div>
                                <div className="mult">{ numbers.format(calc.demandPeakMult, true, 5) }</div>
                                <div className="value">{ numbers.money(calc.demandPeakNoICMS, false) }</div>
                            </div>

                            <div className="line">
                                <div className="label">
                                    Demanda Fora Ponta Sem ICMS (KW)
                                    <InfoTooltip text={tooltipTexts.demandaSemIcms} />
                                </div>
                                <div className="qtty">{ numbers.format(safeData.demands.notConsumed.offPeak) }</div>
                                <div className="mult">{ numbers.format(calc.demandOffPeakMult, true, 5) }</div>
                                <div className="value">{ numbers.money(calc.demandOffPeakNoICMS, false) }</div>
                            </div>

                            { safeData.demands.overtaking.peak !== 0 &&
                                <div className="line">
                                    <div className="label">
                                        Ultrapassagem de Demanda Ponta (KW)
                                        <InfoTooltip text={tooltipTexts.ultrapassagemDemanda} />
                                    </div>
                                    <div className="qtty">{ numbers.format(safeData.demands.overtaking.peak) }</div>
                                    <div className="mult">{ numbers.format(calc.demandOvertakingPeakMult, true, 5) }</div>
                                    <div className="value">{ numbers.money(calc.demandOvertakingPeak, false) }</div>
                                </div>
                            }

                            { safeData.demands.overtaking.offPeak !== 0 &&
                                <div className="line">
                                    <div className="label">
                                        Ultrapassagem de Demanda Fora Ponta (KW)
                                        <InfoTooltip text={tooltipTexts.ultrapassagemDemanda} />
                                    </div>
                                    <div className="qtty">{ numbers.format(safeData.demands.overtaking.offPeak) }</div>
                                    <div className="mult">{ numbers.format(calc.demandOvertakingOffPeakMult, true, 5) }</div>
                                    <div className="value">{ numbers.money(calc.demandOvertakingOffPeak, false) }</div>
                                </div>
                            }
                        </>
                    )
                }

                <div className="line">
                    <div className="label">
                        TUSD Encargos Ponta (KWh)
                        <InfoTooltip text={tooltipTexts.tusdPonta} />
                    </div>
                    <div className="qtty">{ numbers.format(safeData.consumptionPeak) }</div>
                    <div className="mult">{ numbers.format(calc.consumptionPeakMult, true, 5) }</div>
                    <div className="value">{ numbers.money(calc.consumptionPeak, false) }</div>
                </div>

                <div className="line">
                    <div className="label">
                        TUSD Encargos Fora Ponta (KWh)
                        <InfoTooltip text={tooltipTexts.tusdForaPonta} />
                    </div>
                    <div className="qtty">{ numbers.format(safeData.consumptionOffPeak) }</div>
                    <div className="mult">{ numbers.format(calc.consumptionOffPeakMult, true, 5) }</div>
                    <div className="value">{ numbers.money(calc.consumptionOffPeak, false) }</div>
                </div>

                <div className={`line${!hasTeACL ? ' line-alert' : ''}`}>
                    <div className="label">
                        TE ACL (KWh){!hasTeACL ? ' ³' : ''}
                        <InfoTooltip text={tooltipTexts.teAcl} />
                    </div>
                    <div className="qtty">{hasTeACL ? Number(teAclAmount).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                        }) : Number(safeData.consumptionTE ?? 0).toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                            }) }</div>
                    {
                        hasTeACL ? (
                            <>
                                {
                                    (isFiniteNum(teAclTariff) && isFiniteNum(teAclValue)) ? (
                                        <>
                                            <div className="mult">{ numbers.format(Number(teAclTariff), true, 5) }</div>
                                            <div className="value">{  numbers.money(Number(teAclValue), false) }</div>
                                        </>
                                    ) : (
                                        <div className="value plus" dangerouslySetInnerHTML={{ __html: '<span class="default">Dados incompletos</span><span class="mobile">N/D</span>' }}></div>
                                    )
                                }
                            </>
                        ) : (
                            <div className="value plus" dangerouslySetInnerHTML={{ __html: '<span class="default">Dados ainda não disponíveis na CCEE</span><span class="mobile">N/D</span>' }}></div>
                        )
                    }
                </div>

                <div className={`line${!hasPerdaTrans ? ' line-alert' : ''}`}>
                    <div className="label">
                        Perdas de Transmissão 
                        <InfoTooltip text={tooltipTexts.perdasTransmissao} />
                    </div>
                    <div className="qtty">{hasPerdaTrans ? Number(perdaTransAmount).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                        }) : '-' }</div>
                    {
                        hasPerdaTrans ? (
                            <>
                                {
                                    (isFiniteNum(perdaTransTariff) && isFiniteNum(perdaTransValue)) ? (
                                        <>
                                            <div className="mult">{ numbers.format(Number(perdaTransTariff), true, 5) }</div>
                                            <div className="value">{  numbers.money(Number(perdaTransValue), false) }</div>
                                        </>
                                    ) : (
                                        <div className="value plus" dangerouslySetInnerHTML={{ __html: '<span class="default">Dados incompletos</span><span class="mobile">N/D</span>' }}></div>
                                    )
                                }
                            </>
                        ) : (
                            <div className="value plus" dangerouslySetInnerHTML={{ __html: '<span class="default">Dados ainda não disponíveis na CCEE</span><span class="mobile">N/D</span>' }}></div>
                        )
                    }
                </div>

                <div className={`line${hasProinfaAny ? '' : ' line-alert'}`}>
                    <div className="label">
                        Energia Proinfa
                        <InfoTooltip text={tooltipTexts.energiaProinfa} />
                    </div>
                    <div className="qtty">
                        {hasProinfaAmount
                            ? Number(proinfaAmount).toLocaleString("pt-BR", { minimumFractionDigits: 2 })
                            : '-'}
                    </div>
                    {
                        hasProinfaAny ? (
                            <>
                                <div className="mult">
                                    {isFiniteNum(proinfaTariff) ? numbers.format(Number(proinfaTariff), true, 5) : '-'}
                                </div>
                                <div className="value">
                                    {isFiniteNum(proinfaValue) ? numbers.money(Number(proinfaValue), false) : 'N/D'}
                                </div>
                            </>
                        ) : (
                            <div className="value plus" dangerouslySetInnerHTML={{ __html: '<span class="default">Dados ainda não disponíveis na CCEE</span><span class="mobile">N/D</span>' }}></div>
                        )
                    }
                </div>

                <div className="line">
                    <div className="label">
                        Base de Cálculo PIS/COFINS (R$)
                        <InfoTooltip text={tooltipTexts.baseCalculoPisCofins} />
                    </div>
                    <div className="qtty"></div>
                    <div className="mult"></div>
                    <div className="value">{ numbers.money(calc.calcPisCofins, false) }</div>
                </div>

                <div className="line">
                    <div className="label">
                        Base de Cálculo ICMS (R$)
                        <InfoTooltip text={tooltipTexts.baseCalculoIcms} />
                    </div>
                    <div className="qtty"></div>
                    <div className="mult"></div>
                    <div className="value">{ numbers.money(calc.calcIcms, false) }</div>
                </div>

                <div className="line">
                    <div className="label">
                        PIS/COFINS
                        <InfoTooltip text={tooltipTexts.pisCofins} />
                    </div>
                    <div className="qtty"></div>
                    <div className="mult">{ numbers.format(safeData.pisCofins, true) }%</div>
                    <div className="value">{ numbers.money(calc.pisCofins, false) }</div>
                </div>

                <div className="line">
                    <div className="label">
                        ICMS
                        <InfoTooltip text={tooltipTexts.icms} />
                    </div>
                    <div className="qtty"></div>
                    <div className="mult">{ numbers.format(safeData.icms, true) }%</div>
                    <div className="value">{ numbers.money(calc.icms, false) }</div>
                </div>

                <div className={`line ${calc.liqFinc===null && ' line-alert'}`}>
                    <div className="label">
                        Liquidação Financeira
                        <InfoTooltip text={tooltipTexts.liquidacaoFinanceira} />
                    </div>
                    <div className="qtty"></div>
                    <div className="mult"></div>
                    {
                        calc.liqFinc===null ?
                        <div className="value plus" dangerouslySetInnerHTML={{ __html: '<span class="default">Não informado</span><span class="mobile">N/D</span>' }}></div> :
                        <div className="value">{ numbers.money(calc.liqFinc, false) }</div>
                    }
                </div>

                <div className="line">
                    <div className="label">
                        Energia de Reserva
                        <InfoTooltip text={tooltipTexts.energiaReserva} />
                    </div>
                    <div className="qtty"></div>
                    <div className="mult"></div>
                    <div className="value">{ numbers.money((calc.bkpPower===null ? calc.bkpPowerPrev : calc.bkpPower), false) }</div>
                </div>

                {calc.ccee !== 0 &&
                    <div className="line">
                        <div className="label">
                            Taxa da CCEE
                            <InfoTooltip text={tooltipTexts.taxaCcee} />
                        </div>
                        <div className="qtty"></div>
                        <div className="mult"></div>
                        <div className="value">{ numbers.money(calc.ccee, false) }</div>
                    </div>
                }

                {calc.ccee_ercap !== 0 &&
                    <div className="line">
                        <div className="label">
                            Taxa da ERCAP
                            <InfoTooltip text={tooltipTexts.taxaErcap} />
                        </div>
                        <div className="qtty"></div>
                        <div className="mult"></div>
                        <div className="value">{ numbers.money(calc.ccee_ercap, false) }</div>
                    </div>
                }

                {calc.nuclearEnergy !== 0 &&
                    <div className="line">
                        <div className="label">
                            Taxa da Nuclear
                            <InfoTooltip text={tooltipTexts.taxaErcap} />
                        </div>
                        <div className="qtty"></div>
                        <div className="mult"></div>
                        <div className="value">{ numbers.money(calc.nuclearEnergy, false) }</div>
                    </div>
                }

                {
                    calc.feeInterestCorrection !== 0 &&
                    <div className="line">
                        <div className="label">
                            Multa, Juros e Correção (R$)
                            <InfoTooltip text={tooltipTexts.multaJurosCorrecao} />
                        </div>
                        <div className="qtty"></div>
                        <div className="mult"></div>
                        <div className="value">{ numbers.money(calc.feeInterestCorrection, false) }</div>
                    </div>
                }

                {
                    calc.discounts !== 0 &&
                    <div className="line">
                        <div className="label">
                            Descontos (R$)
                            <InfoTooltip text={tooltipTexts.descontos} />
                        </div>
                        <div className="qtty"></div>
                        <div className="mult"></div>
                        <div className="value"> -{ numbers.money(calc.discounts, false) }</div>
                    </div>
                }

                {
                    safeData.others.freeMarket!==0 && (
                        <div className="line">
                            <div className="label">
                                Outros Valores (R$)
                                <InfoTooltip text={tooltipTexts.outrosValores} />
                            </div>
                            <div className="qtty"></div>
                            <div className="mult"></div>
                            <div className="value">{ numbers.money(safeData.others.freeMarket, false) }</div>
                        </div>
                    )
                }

                {
                    safeData.covid !== 0 && (
                        <div className="line">
                            <div className="label">
                                Conta COVID (R$)
                                <InfoTooltip text={tooltipTexts.contaCovid} />
                            </div>
                            <div className="qtty"></div>
                            <div className="mult"></div>
                            <div className="value">{ numbers.money(safeData.covid, false) }</div>
                        </div>
                    )
                }

                <div className="line total">
                    <div className="label">
                        Total
                        <InfoTooltip text={tooltipTexts.total} />
                    </div>
                    <div className="qtty"></div>
                    <div className="mult"></div>
                    <div className="value">{ numbers.money(calc.total, false) }</div>
                </div>
            </div>
        </div>
    );
}

export default MercadoLivreBox;